/**
 * useTheme.ts
 * Hook pour gérer le thème (Dark/Light Mode)
 */
import { useState, useEffect } from 'react';

export type Theme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'snapbooth-theme';

/**
 * Hook personnalisé pour gérer le thème
 */
export function useTheme() {
  // Détecter la préférence système ou charger depuis localStorage
  const getInitialTheme = (): Theme => {
    // D'abord, vérifier si l'attribut data-theme est déjà défini (par le script synchrone dans index.html)
    const existingTheme = document.documentElement.getAttribute('data-theme') as Theme | null;
    if (existingTheme === 'dark' || existingTheme === 'light') {
      return existingTheme;
    }

    // Ensuite, vérifier localStorage
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }

    // Sinon, utiliser la préférence système
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    // Snapbooth Redesign : light par défaut (cohérent avec Canva).
    return 'light';
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // Appliquer le thème au document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Écouter les changements de préférence système (optionnel)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Ne changer que si l'utilisateur n'a pas de préférence sauvegardée
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  return { theme, setTheme, toggleTheme };
}
